import logger from '@/utils/logger'

export function getTelegramWebApp() {
  return window.Telegram?.WebApp ?? null
}

export function isTelegramWebAppActive() {
  return !!getTelegramWebApp()?.initData
}

export function hapticFeedback(type = 'impact', style = 'light') {
  const tg = getTelegramWebApp()
  const haptic = tg?.HapticFeedback
  if (!haptic) return false

  try {
    switch (type) {
      case 'notification':
        haptic.notificationOccurred(style)
        break
      case 'selection':
        haptic.selectionChanged()
        break
      case 'impact':
      default:
        haptic.impactOccurred(style)
        break
    }
    return true
  } catch (error) {
    logger.warn('Haptic feedback not available:', error)
    return false
  }
}

let activeBackHandler = null
let activeWebViewBackHandler = null

function detachBackButtonHandler(tg) {
  if (tg?.BackButton && activeBackHandler && tg.BackButton.offClick) {
    tg.BackButton.offClick(activeBackHandler)
  }

  const webView = window.Telegram?.WebView
  if (webView && activeWebViewBackHandler) {
    if (typeof webView.offEvent === 'function') {
      webView.offEvent('back_button_pressed', activeWebViewBackHandler)
    }
    activeWebViewBackHandler = null
  }

  activeBackHandler = null
}

export function configureBackButton({ visible = true, onClick = null } = {}) {
  const tg = getTelegramWebApp()
  if (!tg) return false

  try {
    detachBackButtonHandler(tg)

    if (tg.BackButton) {
      if (visible && typeof onClick === 'function') {
        activeBackHandler = onClick
        tg.BackButton.onClick(onClick)
        tg.BackButton.show()
      } else {
        tg.BackButton.hide()
      }
      return true
    }

    const webView = window.Telegram?.WebView
    if (!webView?.postEvent) return false

    webView.postEvent('web_app_setup_back_button', false, { is_visible: visible })
    if (visible && typeof onClick === 'function') {
      activeWebViewBackHandler = onClick
      activeBackHandler = onClick
      webView.onEvent('back_button_pressed', onClick)
    }
    return true
  } catch (error) {
    logger.warn('Failed to configure back button:', error)
    return false
  }
}

export function hideBackButton() {
  return configureBackButton({ visible: false })
}

export function cleanupBackButton() {
  const tg = getTelegramWebApp()

  try {
    detachBackButtonHandler(tg)

    if (tg?.BackButton) {
      tg.BackButton.hide()
      return true
    }

    const webView = window.Telegram?.WebView
    if (webView?.postEvent) {
      webView.postEvent('web_app_setup_back_button', false, { is_visible: false })
    }
    return true
  } catch (error) {
    logger.warn('Failed to cleanup back button:', error)
    return false
  }
}

const TELEGRAM_LINK_PATTERN = /^(?:https?:\/\/)?(?:t\.me|telegram\.me)\//i

export function isTelegramNativeLink(url) {
  if (!url) return false
  const normalized = String(url).trim()
  return TELEGRAM_LINK_PATTERN.test(normalized)
    || /^tg:\/\//i.test(normalized)
    || /^@\w[\w\d_]{4,}/i.test(normalized)
}

export function normalizeTelegramLink(url) {
  const normalized = String(url || '').trim()
  if (!normalized) return ''

  if (/^@\w/i.test(normalized)) {
    return `https://t.me/${normalized.slice(1)}`
  }

  if (/^tg:\/\//i.test(normalized)) {
    return normalized
  }

  if (/^(?:t\.me|telegram\.me)\//i.test(normalized)) {
    return `https://${normalized}`
  }

  return normalized
}

/**
 * Open Telegram-native destinations (bots, users, groups, share sheet).
 * Uses WebApp.openTelegramLink — stays inside Telegram.
 */
export function openTelegramLink(url) {
  const link = normalizeTelegramLink(url)
  if (!link) return false

  const tg = getTelegramWebApp()

  try {
    if (isTelegramWebAppActive() && tg?.openTelegramLink) {
      tg.openTelegramLink(link)
      return true
    }
  } catch (error) {
    logger.warn('openTelegramLink not available:', error)
  }

  window.location.href = link
  return false
}

/** Bot API 7.0+: prefer Safari/Chrome over Telegram in-app browser (needed for 3DS / bank apps). */
export const SYSTEM_BROWSER_OPEN_LINK_OPTIONS = { try_browser: true }

/**
 * Open regular web URLs (payment pages, websites).
 * Uses WebApp.openLink — may open in Telegram in-app browser unless options.try_browser is set.
 */
export function openExternalLink(url, options) {
  if (!url) return false

  if (isTelegramNativeLink(url)) {
    return openTelegramLink(url)
  }

  const tg = getTelegramWebApp()

  try {
    if (isTelegramWebAppActive() && tg?.openLink) {
      tg.openLink(url, options)
      return true
    }
  } catch (error) {
    logger.warn('Failed to open external link via Telegram WebApp:', error)
  }

  window.location.href = url
  return false
}

/** Open bank / card payment pages in the device system browser when possible. */
export function openPaymentLinkInSystemBrowser(url) {
  return openExternalLink(url, SYSTEM_BROWSER_OPEN_LINK_OPTIONS)
}

export function bindTelegramEvent(event, handler) {
  const tg = getTelegramWebApp()
  if (!tg?.onEvent || typeof handler !== 'function') return false
  tg.onEvent(event, handler)
  return true
}

export function unbindTelegramEvent(event, handler) {
  const tg = getTelegramWebApp()
  if (!tg?.offEvent || typeof handler !== 'function') return false
  tg.offEvent(event, handler)
  return true
}

export function resetTelegramScroll() {
  try {
    if (window.Telegram?.WebView?.scrollTo) {
      window.Telegram.WebView.scrollTo(0, 0)
    }
  } catch (_) {
    /* ignore */
  }
}

export function hideKeyboard() {
  if (!isTelegramWebAppActive()) return false

  try {
    getTelegramWebApp()?.hideKeyboard?.()
    return true
  } catch (error) {
    logger.warn('hideKeyboard not available:', error)
    return false
  }
}

export function enableClosingConfirmation() {
  if (!isTelegramWebAppActive()) return false

  try {
    getTelegramWebApp()?.enableClosingConfirmation?.()
    return true
  } catch (error) {
    logger.warn('enableClosingConfirmation not available:', error)
    return false
  }
}

export function disableClosingConfirmation() {
  if (!isTelegramWebAppActive()) return false

  try {
    getTelegramWebApp()?.disableClosingConfirmation?.()
    return true
  } catch (error) {
    logger.warn('disableClosingConfirmation not available:', error)
    return false
  }
}

export function showNativeAlert(message) {
  const tg = getTelegramWebApp()
  if (!tg?.showAlert || !message) return Promise.resolve(false)

  return new Promise((resolve) => {
    try {
      tg.showAlert(String(message), () => resolve(true))
    } catch (error) {
      logger.warn('showAlert not available:', error)
      resolve(false)
    }
  })
}

export function showNativeConfirm(message) {
  const tg = getTelegramWebApp()
  if (!tg?.showConfirm || !message) return Promise.resolve(false)

  return new Promise((resolve) => {
    try {
      tg.showConfirm(String(message), (confirmed) => resolve(!!confirmed))
    } catch (error) {
      logger.warn('showConfirm not available:', error)
      resolve(false)
    }
  })
}

export function showNativePopup({ title, message, buttons = [] } = {}) {
  const tg = getTelegramWebApp()
  if (!tg?.showPopup || !message) return Promise.resolve(null)

  return new Promise((resolve) => {
    try {
      tg.showPopup(
        {
          title: title || undefined,
          message: String(message),
          buttons,
        },
        (buttonId) => resolve(buttonId ?? null),
      )
    } catch (error) {
      logger.warn('showPopup not available:', error)
      resolve(null)
    }
  })
}

export function shareViaTelegram({ url, text = '' } = {}) {
  if (!url || !isTelegramWebAppActive()) return false

  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
  return openTelegramLink(shareUrl)
}

/**
 * Open a Telegram Stars invoice inside the Mini App.
 * @returns {Promise<'paid'|'cancelled'|'failed'|'pending'>}
 */
export function openInvoice(invoiceUrl, callback) {
  if (!invoiceUrl) return Promise.resolve('failed')

  const tg = getTelegramWebApp()

  if (!isTelegramWebAppActive() || !tg?.openInvoice) {
    return Promise.resolve('failed')
  }

  return new Promise((resolve) => {
    try {
      tg.openInvoice(invoiceUrl, (status) => {
        if (typeof callback === 'function') {
          callback(status)
        }
        resolve(status || 'failed')
      })
    } catch (error) {
      logger.warn('openInvoice not available:', error)
      resolve('failed')
    }
  })
}
